'use client';

import { useTranslation } from 'react-i18next';

import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { useRouter } from '@sola/ark-core/utils/hooks/useRouter.ts';
import { useSponsorshipsCurrentAndRecent } from '@sola/supporter-sdk/hooks/commitments.ts';

import { SponsorshipLinkCard } from '../../molecules/sponsorship-card/sponsorship-card.tsx';
import { SponsorshipsSkeleton } from './sponsorships-skeleton.tsx';

export const useComponentData = () => {
  const { sponsorships, isLoading, isFetching, isPending, isError } =
    useSponsorshipsCurrentAndRecent();

  const isShowingSkeletons = (isLoading || isFetching || isPending) && !isError;
  const isShowingSponsorships = !isShowingSkeletons && !!sponsorships?.length;
  const hasNoSponsorships =
    !isShowingSkeletons && !isError && sponsorships.length === 0;

  return {
    isShowingSkeletons,
    hasError: isError,
    sponsorships,
    isShowingSponsorships,
    hasNoSponsorships,
  };
};

/**
 * Displays sponsorships sorted alphabetically by child name.
 */
export const SponsorshipsList = () => {
  const { usePathname } = useRouter();
  const { isShowingSkeletons, isShowingSponsorships, sponsorships, hasError } =
    useComponentData();
  const { t } = useTranslation(['my', 'core']);
  const pathname = usePathname();
  const basePath = pathname.replace(/\/$/, '');

  return (
    <div className="SponsorshipsList max-w-md flex flex-col gap-comfy-xl">
      {/* Loading state with skeletons */}
      {isShowingSkeletons && <SponsorshipsSkeleton />}

      {/* Success state - display sponsorships */}
      {isShowingSponsorships && (
        <div className="SponsorshipsList__group flex flex-col gap-comfy-xs max-w-container-lg">
          {sponsorships.map((sponsorship) => (
            <SponsorshipLinkCard
              key={sponsorship.internalId}
              commitment={sponsorship}
              /**
               * TODO: First, we probably want a system for getting/passing the contenPosition for mySponsorships here.
               * Second, the component itself might want to handle this, or at least have a fallback.
               */
              href={`${basePath}/${sponsorship.participant?.globalId}`}
            />
          ))}
        </div>
      )}

      {/* Error state */}
      {hasError && (
        <Alert
          variant="negative"
          title={t('sponsorships.queryError.title')}
          message={t('sponsorships.queryError.message')}
          action={
            <a href={t('my:ctas.contactSupport.url')}>
              {t('my:ctas.contactSupport.text')}
            </a>
          }
        />
      )}
    </div>
  );
};
